import React, { useEffect, useRef, useState } from 'react';
import { useLang } from '../i18n/LanguageContext';
import { ChannelManagerText, channelManagerText } from '../i18n/pages/channelManager';
import {
  MessagePlaceholder,
  MessageTemplate,
  MessageTemplateType,
  getMessagePlaceholders,
  getMessageTemplates,
  previewMessageTemplate,
  resetMessageTemplate,
  saveMessageTemplate,
} from '../api.channelManager';
import './messageTemplateEditor.css';

const OPEN_STORAGE_KEY = (type: string) => `pms_message_${type}_expanded`;

/** One accent per message type, so the three read as distinct cards rather than identical gray ones. */
const ACCENT: Record<MessageTemplateType, { icon: string; color: string; soft: string; text: string }> = {
  FORM_LINK: { icon: '📝', color: '#2563eb', soft: '#eaf0ff', text: '#2b6cb0' },
  GUESTBOOK_REMINDER: { icon: '⏰', color: '#d97706', soft: '#fef3e0', text: '#92400e' },
  GUIDE_READY: { icon: '🔑', color: '#0d9488', soft: '#e6fbf8', text: '#0f766e' },
};

/**
 * Lets a manager write what Guestbook says to their guests.
 *
 * Placeholders are offered from the server rather than hardcoded here, so the list can never drift
 * from what the renderer actually substitutes. Preview is rendered server-side for the same
 * reason: a preview produced by different code from the real send is a preview that can lie.
 *
 * Each message is a collapsible card — three full editors (timing + a multi-line body +
 * placeholders + preview) open at once was too much to scan at a glance. Only the form-link card
 * opens by default; the rest remember their own state in localStorage the same way the Hostex
 * setup guide already does.
 */
const MessageTemplateEditor: React.FC = () => {
  const { lang } = useLang();
  const t = channelManagerText[lang];

  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [placeholders, setPlaceholders] = useState<MessagePlaceholder[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [tpl, ph] = await Promise.all([getMessageTemplates(), getMessagePlaceholders()]);
      setTemplates(tpl);
      setPlaceholders(ph);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) return null;

  return (
    <div style={{ marginTop: 24 }}>
      <div
        style={{
          fontWeight: 700,
          fontSize: '0.75rem',
          color: '#94a3b8',
          textTransform: 'uppercase',
          letterSpacing: '0.08em',
          marginBottom: 8,
          paddingBottom: 6,
          borderBottom: '2px solid #e2e8f0',
        }}
      >
        {t.messagesTitle}
      </div>
      <p style={{ fontSize: '0.85rem', color: '#4a5568', margin: '0 0 16px' }}>{t.messagesIntro}</p>

      {templates.map((template) => (
        <TemplateCard
          key={template.templateType}
          template={template}
          placeholders={placeholders}
          t={t}
          onChanged={(updated) =>
            setTemplates((prev) =>
              prev.map((x) => (x.templateType === updated.templateType ? updated : x))
            )
          }
        />
      ))}
    </div>
  );
};

const TemplateCard: React.FC<{
  template: MessageTemplate;
  placeholders: MessagePlaceholder[];
  t: ChannelManagerText;
  onChanged: (updated: MessageTemplate) => void;
}> = ({ template, placeholders, t, onChanged }) => {
  const [body, setBody] = useState(template.body);
  const [enabled, setEnabled] = useState(template.enabled);
  // ?? rather than trusting the field is always present: a null here means "send immediately"
  // and must not be confused with "the server didn't send this key".
  const [offsetDays, setOffsetDays] = useState<number | null>(template.sendOffsetDays ?? null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [open, setOpen] = useState(() => {
    const stored = localStorage.getItem(OPEN_STORAGE_KEY(template.templateType));
    if (stored !== null) return stored === 'true';
    return template.templateType === 'FORM_LINK';
  });
  const toggleOpen = () => {
    const next = !open;
    setOpen(next);
    localStorage.setItem(OPEN_STORAGE_KEY(template.templateType), String(next));
  };

  const meta: Record<MessageTemplateType, { label: string; when: string }> = {
    FORM_LINK: { label: t.msgFormLink, when: t.msgFormLinkWhen },
    GUESTBOOK_REMINDER: { label: t.msgReminder, when: t.msgReminderWhen },
    GUIDE_READY: { label: t.msgGuideReady, when: t.msgGuideReadyWhen },
  };
  const { label, when } = meta[template.templateType];
  const accent = ACCENT[template.templateType];

  /** Inserts at the cursor rather than appending, so a placeholder lands where it is wanted. */
  const insertPlaceholder = (placeholder: string) => {
    const el = textareaRef.current;
    if (!el) {
      setBody((b) => b + placeholder);
      return;
    }
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const next = body.slice(0, start) + placeholder + body.slice(end);
    setBody(next);
    // Restore the caret after React has repainted, so typing continues after the insert.
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + placeholder.length, start + placeholder.length);
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await saveMessageTemplate(template.templateType, body, enabled, offsetDays);
      onChanged(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm(t.msgConfirmReset)) return;
    try {
      const restored = await resetMessageTemplate(template.templateType);
      setBody(restored.body);
      setEnabled(restored.enabled);
      setOffsetDays(restored.sendOffsetDays ?? null);
      onChanged(restored);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to reset');
    }
  };

  const [previewOpen, setPreviewOpen] = useState(false);

  /** Toggles the preview panel — re-fetches on every open so it reflects unsaved edits. */
  const handleTogglePreview = async () => {
    if (previewOpen) {
      setPreviewOpen(false);
      return;
    }
    setError(null);
    try {
      setPreview(await previewMessageTemplate(body));
      setPreviewOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Preview failed');
    }
  };

  const containsLink = body.includes('{{form_link}}') || body.includes('{{guide_link}}');
  const snippet = enabled ? body.replace(/\s+/g, ' ').trim() : t.msgDisabledSnippet;

  return (
    <div className={`mte-card${open ? ' open' : ''}`} style={{
      ['--mte-accent' as any]: accent.color,
      ['--mte-accent-soft' as any]: accent.soft,
      ['--mte-accent-text' as any]: accent.text,
    }}>
      <div className="mte-head" role="button" tabIndex={0} onClick={toggleOpen}
        onKeyDown={(e) => { if (e.key === 'Enter') toggleOpen(); }}>
        <span className="mte-icon">{accent.icon}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minHeight: 22 }}>
            <span style={{ fontWeight: 700, fontSize: '0.93rem', color: '#2d3748' }}>{label}</span>
            {template.usingDefault && (
              <span
                style={{
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: 999,
                  background: '#eef1f6',
                  color: '#5b6577',
                }}
              >
                {t.msgUsingDefault}
              </span>
            )}
          </div>
          <div style={{ fontSize: '0.79rem', color: '#718096', marginTop: 3 }}>{when}</div>
          <div
            style={{
              fontSize: '0.81rem',
              color: enabled ? '#4a5568' : '#a0aec0',
              fontStyle: enabled ? 'normal' : 'italic',
              marginTop: 4,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {snippet}
          </div>
        </div>
        <label className="mte-switch" onClick={(e) => e.stopPropagation()}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
          <span className="mte-switch-track"></span>
        </label>
        <span className="mte-chevron">▶</span>
      </div>

      {open && (
        <div className="mte-body">
          {template.schedulable && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
                padding: '10px 12px',
                marginBottom: 12,
                marginTop: 12,
                background: accent.soft,
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                fontSize: '0.85rem',
                color: '#4a5568',
              }}
            >
              {template.allowsImmediate ? (
                <>
                  <span style={{ fontWeight: 600 }}>{t.msgTiming}</span>
                  <select
                    value={offsetDays === null ? 'immediate' : 'before'}
                    onChange={(e) => setOffsetDays(e.target.value === 'immediate' ? null : 3)}
                    style={{
                      padding: '5px 9px',
                      border: '1px solid #e2e8f0',
                      borderRadius: 6,
                      fontSize: '0.85rem',
                      background: '#fff',
                      color: '#2d3748',
                    }}
                  >
                    <option value="immediate">{t.msgTimingImmediate}</option>
                    <option value="before">{t.msgTimingBefore}</option>
                  </select>
                </>
              ) : (
                <span style={{ fontWeight: 600 }}>{t.msgTiming} {t.msgTimingBefore}</span>
              )}
              {offsetDays !== null && (
                <>
                  <input
                    type="number"
                    min={0}
                    max={90}
                    value={offsetDays}
                    onChange={(e) => {
                      const parsed = parseInt(e.target.value, 10);
                      setOffsetDays(Number.isNaN(parsed) ? 0 : Math.max(0, Math.min(90, parsed)));
                    }}
                    style={{
                      width: 64,
                      padding: '5px 9px',
                      border: '1px solid #e2e8f0',
                      borderRadius: 6,
                      fontSize: '0.85rem',
                      color: '#2d3748',
                    }}
                  />
                  <span>{offsetDays === 0 ? t.msgTimingSameDay : t.msgTimingDaysBefore}</span>
                  <span style={{ color: '#718096', fontSize: '0.79rem', marginLeft: 'auto' }}>
                    {t.msgTimingHour}
                  </span>
                </>
              )}
            </div>
          )}

          <textarea
            ref={textareaRef}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={7}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              fontSize: '0.87rem',
              fontFamily: 'inherit',
              lineHeight: 1.5,
              color: '#2d3748',
              resize: 'vertical',
            }}
          />

          <div style={{ marginTop: 10 }}>
            <div style={{ fontSize: '0.76rem', color: '#718096', marginBottom: 5 }}>
              {t.msgPlaceholders}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
              {placeholders.map((p) => (
                <button
                  key={p.token}
                  type="button"
                  title={p.description}
                  onClick={() => insertPlaceholder(p.placeholder)}
                  style={{
                    fontSize: '0.74rem',
                    fontFamily: 'ui-monospace, monospace',
                    padding: '3px 8px',
                    borderRadius: 6,
                    border: `1px solid ${accent.soft}`,
                    background: accent.soft,
                    color: accent.text,
                    cursor: 'pointer',
                  }}
                >
                  {p.placeholder}
                </button>
              ))}
            </div>
          </div>

          {containsLink && (
            <div
              style={{
                marginTop: 10,
                padding: '8px 11px',
                background: '#fef9c3',
                border: '1px solid #fde047',
                borderRadius: 8,
                fontSize: '0.79rem',
                color: '#713f12',
              }}
            >
              ⚠️ {t.msgLinkWarning}
            </div>
          )}

          {error && (
            <div style={{ marginTop: 10, fontSize: '0.82rem', color: '#c53030' }}>{error}</div>
          )}
          {saved && (
            <div style={{ marginTop: 10, fontSize: '0.82rem', color: '#2f855a' }}>{t.msgSaved}</div>
          )}

          <div style={{ display: 'flex', gap: 6, marginTop: 12, flexWrap: 'wrap' }}>
            <button
              type="button"
              className="app-btn app-btn-green app-btn-compact"
              disabled={saving || !body.trim()}
              onClick={handleSave}
            >
              {saving ? t.msgSaving : t.msgSave}
            </button>
            <button
              type="button"
              className="app-btn app-btn-blue app-btn-compact"
              aria-pressed={previewOpen}
              onClick={handleTogglePreview}
            >
              {previewOpen ? t.msgHidePreview : t.msgPreview}
            </button>
            {!template.usingDefault && (
              <button
                type="button"
                className="app-btn app-btn-red app-btn-compact"
                onClick={handleReset}
              >
                {t.msgReset}
              </button>
            )}
          </div>

          {previewOpen && preview !== null && (
            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: '0.76rem', color: '#718096', marginBottom: 5 }}>
                {t.msgPreviewTitle}
              </div>
              <div
                style={{
                  padding: '12px 14px',
                  background: '#f7fafc',
                  border: '1px solid #e2e8f0',
                  borderRadius: 8,
                  fontSize: '0.86rem',
                  color: '#2d3748',
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.55,
                }}
              >
                {preview}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MessageTemplateEditor;
